import React, {useContext, useState} from "react";
import {submitContactMessage} from "../../services/api";
import StyleContext from "../../contexts/StyleContext";
import GlobalLoader from "../../components/loader/GlobalLoader";
import {useLoading} from "../../contexts/LoadingContext";
import "./ContactForm.scss";

const EMPTY_FORM = {name: "", email: "", subject: "", message: ""};
const EMAIL_RE   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactForm = () => {
  const {isDark} = useContext(StyleContext);
  const {show: showOverlay, hide: hideOverlay} = useLoading();

  const [form, setForm]             = useState(EMPTY_FORM);
  const [status, setStatus]         = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg]     = useState("");
  const [emailSent, setEmailSent]   = useState(true);

  const onChange = e => {
    const {name, value} = e.target;
    setForm(f => ({...f, [name]: value}));
  };

  const validate = () => {
    if (!form.name.trim())                       return "Please enter your name";
    if (!form.email.trim())                      return "Please enter your email";
    if (!EMAIL_RE.test(form.email.trim()))       return "That email doesn't look right";
    if (!form.subject.trim())                    return "Subject is required";
    if (!form.message.trim())                    return "Message can't be empty";
    return null;
  };

  const onSubmit = async e => {
    e.preventDefault();
    const v = validate();
    if (v) { setErrorMsg(v); setStatus("error"); return; }

    setStatus("submitting");
    setErrorMsg("");
    showOverlay("Sending message");
    try {
      const res = await submitContactMessage({
        name:    form.name.trim(),
        email:   form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setEmailSent(!!res?.email_sent);
      setStatus("success");
      setForm(EMPTY_FORM);
    } catch (err) {
      setErrorMsg(err?.message || "Something went wrong sending your message");
      setStatus("error");
    } finally {
      hideOverlay();
    }
  };

  const themeClass = isDark ? "rdf-dark" : "";

  if (status === "success") {
    return (
      <div className={`rd-contact-form--success ${themeClass}`}>
        <div className="rd-contact-form__check" aria-hidden="true">✓</div>
        <h3>Message received</h3>
        <p>
          Thanks for reaching out — I'll get back to you as soon as I can.
          {!emailSent && (
            <>
              <br />
              <small className="rd-contact-form__hint">
                (Your message is safely saved. Email notification will retry automatically.)
              </small>
            </>
          )}
        </p>
        <button
          type="button"
          className="rdf-button rdf-button--ghost"
          onClick={() => setStatus("idle")}
        >
          Send another
        </button>
      </div>
    );
  }

  const submitting = status === "submitting";

  return (
    <form className={`rdf-form ${themeClass}`} onSubmit={onSubmit} noValidate>
      <div className="rdf-group">
        <label className="rdf-label" htmlFor="cf-name">
          Name <span className="rdf-required">*</span>
        </label>
        <input
          id="cf-name"
          name="name"
          type="text"
          className="rdf-input"
          autoComplete="name"
          value={form.name}
          onChange={onChange}
          disabled={submitting}
          maxLength={120}
          placeholder="Your full name"
          required
        />
      </div>

      <div className="rdf-group">
        <label className="rdf-label" htmlFor="cf-email">
          Email <span className="rdf-required">*</span>
        </label>
        <input
          id="cf-email"
          name="email"
          type="email"
          className="rdf-input"
          autoComplete="email"
          value={form.email}
          onChange={onChange}
          disabled={submitting}
          maxLength={190}
          placeholder="you@example.com"
          required
        />
      </div>

      <div className="rdf-group">
        <label className="rdf-label" htmlFor="cf-subject">
          Subject <span className="rdf-required">*</span>
        </label>
        <input
          id="cf-subject"
          name="subject"
          type="text"
          className="rdf-input"
          value={form.subject}
          onChange={onChange}
          disabled={submitting}
          maxLength={200}
          placeholder="What's this about?"
          required
        />
      </div>

      <div className="rdf-group">
        <label className="rdf-label" htmlFor="cf-message">
          Message <span className="rdf-required">*</span>
        </label>
        <textarea
          id="cf-message"
          name="message"
          rows={6}
          className="rdf-input rdf-textarea"
          value={form.message}
          onChange={onChange}
          disabled={submitting}
          maxLength={5000}
          placeholder="Tell me a bit about the project or idea…"
          required
        />
      </div>

      {status === "error" && errorMsg && (
        <p className="rdf-banner-error" role="alert">{errorMsg}</p>
      )}

      <button type="submit" className="rdf-button" disabled={submitting}>
        {submitting ? (
          <>
            <GlobalLoader inline compact />
            <span>Sending…</span>
          </>
        ) : "Send Message"}
      </button>
    </form>
  );
};

export default ContactForm;
